import { useEffect, useMemo, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";
import PageHero from "../PageHero.jsx";
import Sheet from "../Sheet.jsx";
import { useQuery, useDataEpoch } from "../../lib/dataClient.js";
import { formatCompact } from "../../lib/renderers.js";
import { scopeStepLabel } from "../../lib/dimensionDefs.js";
import { loadClusterTree, leafDescendantIds } from "../../lib/clusterTree.js";
import { rollupActivityMap, ancestorChain } from "../../lib/clusterIcicle.js";
import { CompositionPanel, COMPOSITION_ROWS } from "./Composition.jsx";
import { concentrationVerdict, computeLift, topStandout } from "../../lib/compositionModel.js";
import { useScopeBaseline } from "../../lib/useScopeBaseline.js";
import ScopeTaxonomyMap from "./ScopeTaxonomyMap.jsx";

// The three dims the cluster composition profiles by (who drives this cluster).
const CLUSTER_COMP_DIMS = ["endpoints", "identities", "agents"];

// Cluster VALUE lens — "Detail": the WHOLE cluster taxonomy as an icicle map
// (HierarchyMap) with this cluster pinpointed inside it, plus a composition
// panel of who drives it. The map's geometry (leaf-count spans) and heat
// (scoped activity) behavior lives in HierarchyMap now; this body owns the
// cluster tree + scoped-activity fetch, the header stats, and navigation
// (clicking a band re-roots the cluster scope at that node).

export default function ClusterAnatomyBody({ nav, ctx }) {
  const scopePath = nav?.scopePath || [];
  const terminal = scopePath.length ? scopePath[scopePath.length - 1] : null;
  const clusterId = terminal?.dim === "clusters" ? String(terminal.ids?.[0] ?? terminal.id ?? "") : "";
  const fallbackLabel = terminal ? scopeStepLabel(terminal) : "Topic";

  // Structure — the cached, generation-pin-aware tree.
  const [tree, setTree] = useState(null);
  const epoch = useDataEpoch();
  useEffect(() => {
    let on = true;
    loadClusterTree().then((t) => { if (on) setTree(t); });
    return () => { on = false; };
  }, [clusterId, epoch]);

  // Heat: leaf and subtree activity for the surrounding scope (cluster steps dropped
  // server-side: the map shows the whole taxonomy, the selected cluster is a
  // highlight, not a filter).
  const { data: leafRows } = useQuery("getClusterLeafActivity", {
    scopePath,
    range: ctx?.range,
    includeParents: true,
  });
  const activity = useMemo(() => rollupActivityMap(tree, leafRows || []), [tree, leafRows]);

  const node = tree?.byId.get(clusterId) ?? null;
  const label = node?.name || fallbackLabel;
  const ancestors = useMemo(() => ancestorChain(tree, clusterId), [tree, clusterId]);
  const childIds = tree?.childrenOf.get(clusterId) ?? [];
  const isLeaf = tree ? childIds.length === 0 : false;
  const leafCount = useMemo(
    () => (tree && node ? leafDescendantIds(tree, clusterId).length : 0),
    [tree, node, clusterId],
  );

  // Share of its sibling level, from the same rollup the map shades by.
  const shareOfLevel = useMemo(() => {
    if (!tree || !node) return null;
    const peers = node.parentId != null && tree.byId.has(node.parentId)
      ? (tree.childrenOf.get(node.parentId) ?? [])
      : tree.roots;
    const total = peers.reduce((sum, id) => sum + (activity.get(id) || 0), 0);
    if (total <= 0) return null;
    return Math.round(((activity.get(clusterId) || 0) / total) * 100);
  }, [tree, node, activity, clusterId]);

  // Everything before the first clusters step — navigation re-roots the cluster
  // step under this prefix.
  const prefix = useMemo(() => {
    const i = scopePath.findIndex((s) => s.dim === "clusters");
    return i < 0 ? scopePath : scopePath.slice(0, i);
  }, [scopePath]);
  const goToNode = (id) => ctx?.onNavigatePath?.([...prefix, { dim: "clusters", ids: [String(id)], id: String(id) }]);
  const drillInto = (dim) => (id) =>
    ctx?.onNavigatePath?.([...scopePath, { dim, ids: [String(id)], id: String(id) }]);

  // ── Composition — who drives this cluster. Ranked members of each dim within
  // the cluster's scope (the same entries the sidebar panes list). The
  // concentration verdict answers the decide-question directly: one person's
  // workflow, or org-wide behavior?
  const range = ctx?.range;
  const compEnabled = Boolean(clusterId);
  const { data: epData, loading: epLoading } = useQuery("getDimensionEntries", {
    dimKey: "endpoints", parentScopePath: scopePath, limit: COMPOSITION_ROWS, range,
  }, { enabled: compEnabled });
  const { data: idData, loading: idLoading } = useQuery("getDimensionEntries", {
    dimKey: "identities", parentScopePath: scopePath, limit: COMPOSITION_ROWS, range,
  }, { enabled: compEnabled });
  const { data: agData, loading: agLoading } = useQuery("getDimensionEntries", {
    dimKey: "agents", parentScopePath: scopePath, limit: COMPOSITION_ROWS, range,
  }, { enabled: compEnabled });

  const clusterTotal = activity.get(clusterId) || 0;
  const concentration = useMemo(
    () => concentrationVerdict({
      entries: epData?.entries,
      total: epData?.total,
      scopeTotal: clusterTotal,
      dimKey: "endpoints",
      subject: "this topic",
    }),
    [epData, clusterTotal],
  );

  // Baseline lift — which members over-index on this cluster vs the org. Same
  // signal the generic ScopeComposition adds; wired here too so the cluster
  // Detail page keeps parity with every other dim's Anatomy view.
  const baseline = useScopeBaseline(CLUSTER_COMP_DIMS, range, Boolean(clusterId));
  const { liftByDim, standout } = useMemo(() => {
    const byDim = new Map();
    const columns = [];
    const cols = [["endpoints", epData], ["identities", idData], ["agents", agData]];
    for (const [dimKey, data] of cols) {
      const baseMembers = baseline.baseByDim.get(dimKey);
      const m = new Map();
      const rows = [];
      for (const e of data?.entries || []) {
        const id = String(e.id);
        const lift = computeLift({
          scopeActivity: Number(e.activity) || 0,
          scopeTotal: clusterTotal,
          baseActivity: baseMembers?.get(id) || 0,
          baseTotal: baseline.baseTotal,
        });
        if (lift != null) m.set(id, lift);
        rows.push({ label: e.label, lift });
      }
      byDim.set(dimKey, m);
      columns.push({ dimLabel: dimKey, rows });
    }
    return { liftByDim: byDim, standout: topStandout(columns) };
  }, [epData, idData, agData, baseline, clusterTotal]);

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]">
      <PageHero
        eyebrow="Topic"
        title={label}
        detail={
          ancestors.length
            ? `${ancestors.map((a) => a.name).join(" › ")} › ${label}`
            : "See this topic in the full taxonomy. Band size shows structure; shading shows activity in this scope."
        }
        metrics={[
          {
            label: "Prompts",
            value: activity.get(clusterId) ? formatCompact(activity.get(clusterId)) : "–",
            note: "in this scope + range",
          },
          {
            label: "Share of level",
            value: shareOfLevel != null ? `${shareOfLevel}%` : "–",
            note: "of its sibling group's activity",
          },
          {
            label: "Children",
            value: tree ? String(childIds.length) : "–",
            note: isLeaf && tree ? "leaf; prompts are direct members" : "direct sub-topics",
          },
          { label: "Leaves", value: tree && node ? String(leafCount) : "–", note: "assignment-carrying descendants" },
        ]}
      />
      {/* The taxonomy atlas with this cluster pinned inside it — the same map
          every scope's Anatomy shows, here with the node highlighted and
          clicks re-rooting the cluster scope. */}
      <ScopeTaxonomyMap
        scopePath={scopePath}
        range={range}
        selectedClusterId={clusterId}
        onNavigateCluster={goToNode}
        eyebrow="Taxonomy"
        title="Topic map"
        clickHint="click a band to navigate"
      />

      {/* Composition — who drives this cluster. The concentration line is the
          decide-question up front; the panels are the evidence, and every row
          narrows the scope to cluster ∩ member. */}
      <Sheet eyebrow="Composition" title="Who drives this topic" tight>
        {concentration ? (
          <span className="block pb-[4px] text-[12px] leading-[1.45] text-[color:var(--muted)]">{concentration}</span>
        ) : null}
        {/* The one thing worth noticing — the strongest over-index vs the org. */}
        {standout ? (
          <span className="block pb-[6px] text-[12px] leading-[1.45] text-[color:var(--ink)]">
            Stands out:{" "}
            <span className="text-[color:var(--green)]">{standout.label}</span> over-indexes{" "}
            <span className="tabular-nums text-[color:var(--green)]">{standout.lift >= 10 ? "10+" : standout.lift.toFixed(1)}×</span>{" "}
            on this topic vs the org ({standout.dimLabel}).
          </span>
        ) : null}
        {/* Always three columns — the panels truncate labels gracefully (min-w-0
            + truncate), so even narrow panes read better as a tight triptych
            than as a stacked list. Each row carries a lift badge vs the org. */}
        <div className="grid gap-x-[18px] gap-y-[14px] [grid-template-columns:repeat(3,minmax(0,1fr))]">
          <CompositionPanel
            title="Endpoints"
            data={epData}
            loading={epLoading}
            scopeTotal={clusterTotal}
            onDrill={drillInto("endpoints")}
            liftById={liftByDim.get("endpoints")}
          />
          <CompositionPanel
            title="Identities"
            data={idData}
            loading={idLoading}
            scopeTotal={clusterTotal}
            onDrill={drillInto("identities")}
            liftById={liftByDim.get("identities")}
          />
          <CompositionPanel
            title="Agents"
            data={agData}
            loading={agLoading}
            scopeTotal={clusterTotal}
            onDrill={drillInto("agents")}
            liftById={liftByDim.get("agents")}
          />
        </div>
      </Sheet>
    </ScrollArea>
  );
}
